import React, { useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import {
  GraduationCap,
  Headphones,
  Mail,
  Video,
  Link2,
  ExternalLink,
  PlayCircle,
  UserCog,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/Card";
import { AnnouncementsPanel } from "../Dashboard/components/AnnouncementsPanel";
import { useAuth } from "../../components/contexts/AuthContext";
import { colors, neutrals } from "../../theme/theme";
import { FormIcon } from "../../components/icons/FormIcon";

// Palette uniforme para todas las tarjetas (estilo vídeos)
const uniformPalette = {
  start: "#ffffff",
  end: "#ffffff",
  text: neutrals.gray[900],
  iconColor: colors.primary.main,
  iconBackground: "rgba(230, 0, 126, 0.1)",
  border: `1px solid ${colors.primary.main}20`,
};

// Enlaces directos y formularios (juntos)
const quickLinks = [
  {
    id: "academy",
    title: "Acceso Academia",
    description: "Formación y cursos SERSEO",
    icon: GraduationCap,
    url: "https://academia.serseo.es/",
    palette: uniformPalette,
  },
  {
    id: "webmail",
    title: "Acceso Webmail",
    description: "Correo corporativo",
    icon: Mail,
    url: "https://webmail.serseo.es/",
    palette: uniformPalette,
  },
  {
    id: "meetings",
    title: "Sala Reuniones OTR",
    description: "Acceso directo a Zoom",
    icon: Video,
    url: "https://us02web.zoom.us/j/86421439417",
    palette: uniformPalette,
  },
  {
    id: "helpdesk",
    title: "Acceso Helpdesk",
    description: "Soporte y gestión de tickets",
    icon: Headphones,
    url: "https://helpdesk.serseo.es/",
    palette: uniformPalette,
  },
  {
    id: "kitdigital",
    title: "Kit Digital",
    description: "Formulario Kit Digital",
    icon: FormIcon,
    url: "https://tally.so/r/3xVG9r",
    palette: uniformPalette,
  },
  {
    id: "chatwith",
    title: "Chatwith",
    description: "Formulario Chatwith",
    icon: FormIcon,
    url: "https://tally.so/r/woGejM",
    palette: uniformPalette,
  },
  {
    id: "otr",
    title: "OTR",
    description: "Formulario OTR",
    icon: FormIcon,
    url: "https://docs.google.com/forms/d/e/1FAIpQLSdbIWYpo39KWbP-4ILVYMylsy7akK0vf7Nh6SQnNiD7KhrNaA/viewform",
    palette: uniformPalette,
  },
  {
    id: "freelancers",
    title: "Freelancers",
    description: "Lista de proveedores/freelancers",
    icon: UserCog,
    url: "https://app.notion.com/p/serseomarketing/408fb795443c805cbf38f71ca56857ab?v=bb3fb795443c82eb9003089b72d9f722",
    palette: uniformPalette,
  },
];

// Componente para tarjetas con gradiente (enlaces)
function GradientLinkCard({ link, onClick }) {
  const Icon = link.icon;

  const cardContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        p: 2.5,
        borderRadius: "12px",
        height: "100%",
        textDecoration: "none",
        color: link.palette.text,
        background: link.palette.border
          ? link.palette.start
          : `linear-gradient(130deg, ${link.palette.start}, ${link.palette.end})`,
        border: link.palette.border || 'none',
        transition: "box-shadow 200ms ease",
        position: "relative",
        cursor: "pointer",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Botón indicador - Solo si hay URL externa */}
      {link.url && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: "4px",
            backgroundColor: link.palette.iconBackground,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.8,
          }}
        >
          <ExternalLink size={12} color={link.palette.text} />
        </Box>
      )}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          backgroundColor: link.palette.iconBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={24} color={link.palette.iconColor || link.palette.text} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: link.palette.text,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {link.title}
        </Typography>
        <Typography
          sx={{
            color: link.palette.text,
            opacity: 0.85,
            fontSize: "0.875rem",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {link.description}
        </Typography>
      </Box>
    </Box>
  );

  // Si es enlace externo
  if (link.url) {
    return (
      <Box
        component="a"
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "block", height: "100%", textDecoration: "none" }}
      >
        {cardContent}
      </Box>
    );
  }

  // Si es ruta interna
  if (link.internalRoute) {
    return (
      <Box
        onClick={onClick}
        sx={{ display: "block", height: "100%", textDecoration: "none" }}
      >
        {cardContent}
      </Box>
    );
  }

  return cardContent;
}

export function ZonaSerseo() {
  const { setPageContext, dashboardRefreshKey } = useOutletContext();
  const { user, interceptorReady } = useAuth();
  const navigate = useNavigate();
  const isAnnouncementAdmin =
    user?.role === "superadmin" || user?.role === "agency_admin";

  useEffect(() => {
    setPageContext({
      title: "Zona SERSEO",
      subtitle: "Tu centro de accesos directos y comunicaciones internas",
      showTitle: true,
    });
  }, [setPageContext]);

  const handleCardClick = (link) => {
    if (link.internalRoute) {
      navigate(link.internalRoute);
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Enlaces directos */}
        <Card
          sx={{
            border: `1px solid ${neutrals.gray[200]}`,
          }}
        >
          <CardHeader sx={{ pb: "12px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Link2 size={18} />
                <CardTitle sx={{ fontSize: "1.1rem" }}>
                  Enlaces directos
                </CardTitle>
              </Box>
            </Box>
          </CardHeader>

          <CardContent sx={{ pt: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",  // 1 columna en móvil
                  sm: "repeat(2, 1fr)",  // 2 columnas en tablet pequeño
                  md: "repeat(3, 1fr)",  // 3 columnas en tablet grande
                  lg: "repeat(4, 1fr)",  // 4 columnas en desktop
                },
                gap: 3,
              }}
            >
              {quickLinks.map((link) => (
                <Box key={link.id}>
                  <GradientLinkCard
                    link={link}
                    onClick={() => handleCardClick(link)}
                  />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Anuncios - Solo visible para agency_admin y superadmin */}
        {user && isAnnouncementAdmin && (
          <Box sx={{ width: "100%", maxWidth: "100%" }}>
            <AnnouncementsPanel
              currentUser={user}
              interceptorReady={interceptorReady}
              dashboardRefreshKey={dashboardRefreshKey}
              maxHeight={isAnnouncementAdmin ? "none" : undefined}
              forceAdminView={true}
              noHeaderGap={true}
              descriptionLines={3}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
